import { useEffect, useState } from 'react';
import { wsClient } from './WebSocketClient';
import type {
  WsChannelStatusChangedPayload,
  WsChannelQRUpdatedPayload,
  ZapyInstanceStatusType,
} from '../types/chat.types';

/**
 * Channel connection status from WebSocket
 */
export interface ChannelConnectionStatus {
  status: ZapyInstanceStatusType;
  qrCode?: string;
  qrExpiresAt?: number;
  profileName?: string;
  ownerJid?: string;
}

/**
 * useChannelSocket return type
 */
export interface UseChannelSocketReturn {
  /** Current channel connection status */
  connectionStatus: ChannelConnectionStatus | null;
  /** Whether subscribed to the channel connection */
  isSubscribed: boolean;
  /** Error from subscription */
  error: Error | null;
}

/**
 * Hook for channel connection WebSocket subscription.
 * Subscribes to a channel connection on mount and listens for status
 * and QR code updates in real-time.
 *
 * @param channelConnectionId - ID of the channel connection to subscribe to
 * @returns Subscription state with connection status
 *
 * @example
 * ```tsx
 * function ZapyConnectionStatus({ channelConnectionId }: { channelConnectionId: number }) {
 *   const { connectionStatus, isSubscribed, error } = useChannelSocket(channelConnectionId);
 *
 *   if (error) return <div>Connection error</div>;
 *   if (!isSubscribed) return <div>Connecting...</div>;
 *
 *   return (
 *     <div>
 *       <Badge>Status: {connectionStatus?.status}</Badge>
 *       {connectionStatus?.qrCode && <QRCode value={connectionStatus.qrCode} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useChannelSocket(channelConnectionId: number | undefined): UseChannelSocketReturn {
  const [connectionStatus, setConnectionStatus] = useState<ChannelConnectionStatus | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!channelConnectionId) {
      setIsSubscribed(false);
      return;
    }

    const handleSubscribed = (payload: { channelConnectionId: number }) => {
      if (payload.channelConnectionId === channelConnectionId) {
        setIsSubscribed(true);
        setError(null);
      }
    };

    const handleStatusChanged = (payload: WsChannelStatusChangedPayload) => {
      if (payload.channelConnectionId === channelConnectionId) {
        setConnectionStatus((prev) => ({
          ...prev,
          status: payload.status,
          profileName: payload.profileName,
          ownerJid: payload.ownerJid,
        }));
      }
    };

    const handleQRUpdated = (payload: WsChannelQRUpdatedPayload) => {
      if (payload.channelConnectionId === channelConnectionId) {
        setConnectionStatus((prev) => ({
          ...prev,
          status: prev?.status || 'pending_qr_code_scan',
          qrCode: payload.qrCode,
          qrExpiresAt: payload.expiresAt,
        }));
      }
    };

    const handleError = (err: Error) => {
      setError(err);
    };

    wsClient.on('channel:subscribed', handleSubscribed);
    wsClient.on('channel:status-changed', handleStatusChanged);
    wsClient.on('channel:qr-updated', handleQRUpdated);
    wsClient.on('error', handleError);

    // Subscribe to the channel connection
    wsClient.subscribeToChannelConnection(channelConnectionId);

    return () => {
      wsClient.off('channel:subscribed', handleSubscribed);
      wsClient.off('channel:status-changed', handleStatusChanged);
      wsClient.off('channel:qr-updated', handleQRUpdated);
      wsClient.off('error', handleError);

      // Unsubscribe from the channel connection
      wsClient.unsubscribeFromChannelConnection(channelConnectionId);
      setIsSubscribed(false);
    };
  }, [channelConnectionId]);

  return {
    connectionStatus,
    isSubscribed,
    error,
  };
}
